/**
 * Drawer-ul unei plati: suma, facturile, comentariile, atasamentele si lantul de amanari.
 *
 * Trei decizii care se vad direct in cod:
 *
 *   1. **Comentariile stau intr-un panou PERMANENT, nu in tooltip la hover.** Asta era problema
 *      reala a celor 594 de comentarii din fisier: informatie care exista, dar pe care nu o gaseai
 *      decat daca nimereai celula cu mouse-ul.
 *   2. **Facturile nu stau aici.** Ele au coloana lor, mereu vizibila in grila; drawer-ul doar le
 *      repeta pentru context. Daca ar exista doar aici, am fi reprodus exact bugul de mai sus.
 *   3. **Amanarea se alege inline, cu data-tinta SCRISA COMPLET.** Nimeni nu trebuie sa ghiceasca
 *      daca „ziua urmatoare" de vineri inseamna sambata sau luni.
 *
 * `Esc` inchide si readuce focusul pe randul din grila (apelantul face restaurarea).
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Paperclip, Trash2, X } from 'lucide-react';
import { formatDayLabel, formatMinor, formatTimestamp, plural } from '@evomag/core/format';
import { AmountInput } from '../../components/AmountInput.tsx';
import { Money } from '../../components/Money.tsx';
import { StatusToken } from '../../components/StatusToken.tsx';
import { ErrorState } from '../../components/ErrorState.tsx';
import { SkeletonRows } from '../../components/SkeletonRows.tsx';
import { useFirComentarii, usePostComentariu, useRezolvaComentariu } from '../../hooks/useComentarii.ts';
import { useAtasamente, useIncarcaAtasament } from '../../hooks/useAtasamente.ts';
import { useAmanare, useLantAmanari } from '../../hooks/useAmanare.ts';
import { useStergePlata } from '../../hooks/useZi.ts';
import type { DayEntryDto } from '../../hooks/useZi.ts';
import { etichetaStare } from '../../lib/stari.ts';
import { etichetaBanca } from './filtre.ts';

export interface PropsDrawerPlata {
  plata: DayEntryDto;
  data: string;
  /** Ziua lucratoare urmatoare, deja calculata din calendarul bancar. */
  ziuaUrmatoare: string | null;
  onClose: () => void;
  onSchimbaSuma: (minor: number, expresie: string | null) => void;
  poateScrie: boolean;
}

const MOTIVE_RAPIDE = ['lipsă disponibil', 'în așteptare aprobare', 'de verificat recepția'] as const;

export function DrawerPlata({
  plata,
  data,
  ziuaUrmatoare,
  onClose,
  onSchimbaSuma,
  poateScrie,
}: PropsDrawerPlata) {
  const refPanou = useRef<HTMLElement>(null);
  const [textComentariu, setTextComentariu] = useState('');
  const [motivAmanare, setMotivAmanare] = useState('');
  const [dataExacta, setDataExacta] = useState('');
  // Cat se amana. Gol = toata plata, comportamentul de pana acum.
  const [sumaAmanata, setSumaAmanata] = useState<number | null>(null);
  // Stergerea se deschide, nu se executa la primul click: un buton care sterge din prima
  // intr-un instrument de trezorerie e o greseala asteptand sa se intample.
  const [confirmaStergere, setConfirmaStergere] = useState(false);
  const [motivStergere, setMotivStergere] = useState('');

  const fir = useFirComentarii('payment_entry', plata.id);
  const atasamente = useAtasamente('payment_entry', [plata.id]);
  const lant = useLantAmanari(plata.postponeCount > 0 ? plata.id : null);
  const postComentariu = usePostComentariu();
  const rezolva = useRezolvaComentariu();
  const incarca = useIncarcaAtasament();
  const amana = useAmanare();
  const sterge = useStergePlata();

  useEffect(() => {
    refPanou.current?.focus();
  }, [plata.id]);

  const aleAcestei = (atasamente.data?.attachments ?? []).filter((a) => a.entityId === plata.id);
  const moneda = plata.currency === 'RON' ? 'lei' : plata.currency.toLowerCase();

  function trimiteComentariu(): void {
    const corp = textComentariu.trim();
    if (corp === '') return;
    postComentariu.mutate(
      { entityType: 'payment_entry', entityId: plata.id, body: corp },
      { onSuccess: () => setTextComentariu('') },
    );
  }

  function amanaLa(mod: 'auto_next_business_day' | 'explicit_date'): void {
    amana.mutate({
      entryId: plata.id,
      dinData: data,
      amanare: {
        version: plata.version,
        appVersion: plata.appVersion,
        mode: mod,
        targetDate: mod === 'explicit_date' ? dataExacta : null,
        // Egal cu totalul inseamna amanare intreaga, si serverul o trateaza ca atare — nu trimitem
        // un partial care nu e partial.
        ...(sumaAmanata !== null && sumaAmanata > 0 && sumaAmanata < plata.amountMinor
          ? { amountMinor: sumaAmanata }
          : {}),
        reason: motivAmanare.trim() === '' ? null : motivAmanare.trim(),
      },
    });
  }

  return (
    <aside
      ref={refPanou}
      tabIndex={-1}
      role="complementary"
      aria-label={`Detalii plată ${plata.supplierName}`}
      className="drawer sticky top-0 flex h-dvh shrink-0 flex-col gap-3 overflow-y-auto p-3"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="text-14">
            <Link
              to="/furnizori/$id"
              params={{ id: String(plata.supplierId) }}
              className="hover:underline"
            >
              {plata.supplierName}
            </Link>
          </h2>
          <span className="num text-11 text-ink-3">
            {plata.cuiNorm ?? 'fără CUI'} · {etichetaBanca(plata)}
          </span>
        </div>
        <button type="button" className="hit-24 chip" onClick={onClose} aria-label="Închide panoul">
          <X size={12} aria-hidden="true" />
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <StatusToken stare={plata.status} varianta="complet" />
        {plata.blockedReason ? (
          <span className="text-11 text-ink-2">{plata.blockedReason}</span>
        ) : null}
      </div>

      {plata.hasSupplierRule ? (
        <div
          data-stare="verify"
          className="rounded-[var(--radius-md)] border border-hairline bg-stare-tint p-2 text-12"
          role="note"
        >
          <strong className="font-medium">Regulă de furnizor.</strong>{' '}
          <Link to="/furnizori/$id" params={{ id: String(plata.supplierId) }} className="underline">
            Vezi regulile fixate pe furnizor
          </Link>
          .
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="text-11 text-ink-2 uppercase">Sumă</span>
        <AmountInput
          value={plata.amountMinor}
          moneda={moneda}
          disabled={!poateScrie}
          eticheta={`Sumă de plată pentru ${plata.supplierName}`}
          expresie={plata.amountExpression}
          onCommit={(minor, expr) => {
            if (minor !== null) onSchimbaSuma(minor, expr);
          }}
        />
        {plata.currency !== 'RON' ? (
          <span className="text-11 text-ink-3">
            {plata.amountRonMinor === 0 && plata.manualRateE6 === null && plata.planningRateE6 === null
              ? 'Fără curs — nu intră în „De plată”.'
              : `= ${formatMinor(plata.amountRonMinor)} lei`}
            {plata.fxTransactionId !== null ? (
              <>
                {' · '}
                <Link
                  to="/valutar/$data"
                  params={{ data }}
                  search={{ drawer: String(plata.fxTransactionId) }}
                  className="underline"
                >
                  vezi tranzacția valutară
                </Link>
              </>
            ) : null}
          </span>
        ) : null}
      </div>

      <section className="flex flex-col gap-1">
        <h3 className="text-11 text-ink-2 uppercase">Facturi</h3>
        <div className="flex flex-wrap gap-1">
          {plata.invoiceRefs.length === 0 ? (
            <span className="text-12 text-ink-3">Nicio factură înregistrată.</span>
          ) : (
            plata.invoiceRefs.map((f) => (
              <span
                key={f.id}
                className="chip num"
                title={f.confidence === 'inferred' ? 'Extrasă automat din comentariu' : undefined}
              >
                {f.raw}
              </span>
            ))
          )}
        </div>
      </section>

      {/* Amanarea: doua cai, inline, cu data-tinta scrisa complet. */}
      {poateScrie ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-11 text-ink-2 uppercase">Amână</h3>

          {/* Cat se amana. Gol = tot, ca pana acum — feature-ul nu adauga un pas obligatoriu la
              o operatie care se face de zeci de ori pe zi. Cand scrii o suma mai mica, randul de
              azi RAMANE viu cu restul; textul de dedesubt o spune inainte, nu dupa. */}
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-11 text-ink-2">
              Cât amâni <span className="text-ink-3">(gol = tot)</span>
              <AmountInput
                value={sumaAmanata}
                moneda={moneda}
                eticheta={`Suma amânată pentru ${plata.supplierName}`}
                onCommit={(minor) => setSumaAmanata(minor)}
              />
            </label>
            {sumaAmanata !== null && sumaAmanata > 0 && sumaAmanata < plata.amountMinor ? (
              <span className="text-11 text-ink-2">
                Rămâne azi{' '}
                <Money minor={plata.amountMinor - sumaAmanata} className="text-11" />, tot cu
                starea de acum.
              </span>
            ) : sumaAmanata !== null && sumaAmanata >= plata.amountMinor ? (
              <span className="text-11 text-ink-2">Se amână toată plata.</span>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Data-țintă e SCRISĂ COMPLET: nimeni nu trebuie să ghicească dacă „ziua următoare”
                de vineri înseamnă sâmbătă sau luni, sau dacă sare o punte. */}
            <button
              type="button"
              className="hit-24 chip font-medium text-ink-1"
              disabled={amana.isPending || ziuaUrmatoare === null}
              title={
                ziuaUrmatoare === null
                  ? 'Calendarul bancar nu acoperă intervalul acesta. Populează-l din /setări — o amânare într-un interval nepopulat eșuează, nu ghicește.'
                  : undefined
              }
              onClick={() => amanaLa('auto_next_business_day')}
            >
              {ziuaUrmatoare === null
                ? 'Ziua lucrătoare următoare — calendar nepopulat'
                : `Ziua lucrătoare următoare — ${formatDayLabel(ziuaUrmatoare)}`}
            </button>
            <label className="inline-flex items-center gap-1 text-12">
              <span className="doar-sr">Dată exactă</span>
              <input
                type="date"
                className="input num"
                value={dataExacta}
                onChange={(e) => setDataExacta(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="hit-24 chip"
              disabled={amana.isPending || dataExacta === ''}
              onClick={() => amanaLa('explicit_date')}
            >
              Amână la data exactă
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {MOTIVE_RAPIDE.map((m) => (
              <button
                key={m}
                type="button"
                className={`hit-24 chip ${motivAmanare === m ? 'font-medium text-ink-1' : ''}`}
                aria-pressed={motivAmanare === m}
                onClick={() => setMotivAmanare(motivAmanare === m ? '' : m)}
              >
                {m}
              </button>
            ))}
          </div>
          {amana.isError ? (
            <p className="text-11 text-neg" role="alert">
              {amana.error.message}
            </p>
          ) : null}
          <p className="text-11 text-ink-3">
            Data scadenței nu se modifică — se mută doar data plății planificate.
          </p>
        </section>
      ) : null}

      {plata.postponeCount > 0 ? (
        <section className="flex flex-col gap-1">
          <h3 className="text-11 text-ink-2 uppercase">
            Istoric amânări <span className="badge num">↻{plata.postponeCount}</span>
          </h3>
          {lant.isPending ? (
            <SkeletonRows numar={2} />
          ) : lant.data ? (
            <>
              {/* Un `hop` e un NOD pe lant (o intrare intr-o zi), nu o muchie: sageata dintre
                  randuri e amanarea, iar motivul apartine nodului din care s-a plecat. */}
              <ol className="flex flex-col gap-1 text-12">
                {lant.data.hops.map((h) => (
                  <li key={h.entryId} className="flex flex-wrap items-baseline gap-1">
                    {h.hop > 0 ? (
                      <span aria-hidden="true" className="text-ink-3">
                        →
                      </span>
                    ) : null}
                    <span className="num">{formatDayLabel(h.businessDate)}</span>
                    {h.decidedByName ? (
                      <>
                        <span className="text-ink-3">·</span>
                        <span className="text-ink-2">{h.decidedByName}</span>
                      </>
                    ) : h.wasAutomatic ? (
                      <span className="badge">automat</span>
                    ) : null}
                    {h.reason ? <span className="text-ink-3">„{h.reason}”</span> : null}
                  </li>
                ))}
              </ol>
              <p className="text-11 text-ink-3">
                Întârziere cumulată:{' '}
                <span className="num">
                  {plural(
                    lant.data.summary.totalBusinessDaysDelayed,
                    'zi lucrătoare',
                    'zile lucrătoare',
                    'de zile lucrătoare',
                  )}
                </span>
                {lant.data.dueDateStable ? null : ' · scadența a fost modificată pe lanț'}
              </p>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="flex flex-col gap-2">
        <h3 className="text-11 text-ink-2 uppercase">
          Comentarii{' '}
          {plata.unresolvedCommentCount > 0 ? (
            <span className="badge num">{plata.unresolvedCommentCount} nerezolvate</span>
          ) : null}
        </h3>

        {fir.isPending ? (
          <SkeletonRows numar={2} />
        ) : fir.isError ? (
          <ErrorState
            titlu="Nu am putut încărca firul"
            detaliu={fir.error.message}
            onReincearca={() => void fir.refetch()}
          />
        ) : (fir.data?.comments.length ?? 0) === 0 ? (
          <p className="text-12 text-ink-3">Niciun comentariu încă.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {fir.data?.comments.map((c) => (
              <li key={c.id} className="flex flex-col gap-0.5 border-b border-hairline pb-2 last:border-0">
                <span className="flex flex-wrap items-baseline gap-1 text-11 text-ink-3">
                  <span className="font-medium text-ink-2">{c.authorDisplay}</span>
                  <span className="num">
                    {c.sourceCreatedAt ?? formatTimestamp(c.createdAt)}
                  </span>
                  {/* `xlsx_note` = notă clasică, `xlsx_threaded` = comentariu cu autor și dată din
                      Google Sheets. Ambele au venit din fișier; `app` e scris aici. */}
                  {c.source.startsWith('xlsx') ? <span className="badge">din Excel</span> : null}
                  {c.resolvedAt !== null ? <span className="badge">rezolvat</span> : null}
                </span>
                <p className="text-12 whitespace-pre-wrap">{c.body}</p>
                {poateScrie ? (
                  <button
                    type="button"
                    className="hit-24 chip self-start"
                    onClick={() => rezolva.mutate({ id: c.id, resolved: c.resolvedAt === null })}
                  >
                    {c.resolvedAt === null ? 'Marchează rezolvat' : 'Redeschide'}
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
        )}

        {poateScrie ? (
          <>
            <label className="flex flex-col gap-1">
              <span className="doar-sr">Adaugă un comentariu</span>
              <textarea
                className="input min-h-16 py-1"
                placeholder="Scrie un comentariu… (numerele de factură se extrag automat)"
                value={textComentariu}
                onChange={(e) => setTextComentariu(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    trimiteComentariu();
                  }
                }}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="hit-24 chip font-medium text-ink-1"
                disabled={postComentariu.isPending || textComentariu.trim() === ''}
                onClick={trimiteComentariu}
              >
                {postComentariu.isPending ? 'Se trimite…' : 'Trimite'}
              </button>
              <label className="hit-24 chip cursor-pointer">
                <Paperclip size={12} aria-hidden="true" /> Alege fișiere
                <input
                  type="file"
                  className="doar-sr"
                  multiple
                  onChange={(e) => {
                    for (const fisier of Array.from(e.target.files ?? [])) {
                      incarca.mutate({ entityType: 'payment_entry', entityId: plata.id, fisier });
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            {postComentariu.isError ? (
              <p className="text-11 text-neg" role="alert">
                {postComentariu.error.message}
              </p>
            ) : null}
            {incarca.isError ? (
              <p className="text-11 text-neg" role="alert">
                {incarca.error.message}
              </p>
            ) : null}
          </>
        ) : null}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-11 text-ink-2 uppercase">
          Atașamente <span className="num text-ink-3">{aleAcestei.length}</span>
        </h3>
        {aleAcestei.length === 0 ? (
          <p className="text-12 text-ink-3">Niciun fișier atașat.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {aleAcestei.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 text-12">
                <a href={a.downloadUrl} className="truncate underline" download>
                  {a.filename}
                </a>
                <span className="flex shrink-0 items-center gap-1 text-11 text-ink-3">
                  {a.label ? <span className="chip">{a.label}</span> : null}
                  <span className="num">{Math.round(a.sizeBytes / 1024)} KB</span>
                  {a.scanStatus === 'pending' ? (
                    <span className="badge" title="Nu există încă scanare antivirus">
                      nescanat
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* --- stergere ------------------------------------------------------------------------
          Sta jos si in doi pasi, deliberat. Motivul e OBLIGATORIU pentru ca jurnalul sa poata
          raspunde la „s-a sters din greseala?" — un rand de jurnal fara motiv muta intrebarea de
          la „de ce" la „cine sa mai stie". Randul nu dispare definitiv: se poate restaura din
          Administrare > Jurnal, iar textul de aici o spune, ca sa nu para o decizie ireversibila. */}
      {poateScrie ? (
        <section className="flex flex-col gap-2 border-t border-hairline pt-2">
          {!confirmaStergere ? (
            <button
              type="button"
              className="hit-24 chip self-start text-neg"
              onClick={() => setConfirmaStergere(true)}
            >
              <Trash2 size={12} aria-hidden="true" />
              Șterge plata
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-11 text-ink-2">
                Se șterge plata către <strong>{plata.supplierName}</strong> de{' '}
                <Money minor={plata.amountRonMinor} className="text-11" />. Rândul rămâne
                recuperabil din <em>Administrare · Jurnal</em>.
              </p>
              <label className="flex flex-col gap-1 text-11 text-ink-2">
                Motiv (obligatoriu)
                <input
                  className="input"
                  autoFocus
                  placeholder="stornată de furnizor, dublă înregistrare…"
                  value={motivStergere}
                  onChange={(e) => setMotivStergere(e.target.value)}
                />
              </label>
              {sterge.isError ? (
                <span className="text-11 text-neg" role="alert">
                  {sterge.error.message}
                </span>
              ) : null}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="hit-24 chip"
                  onClick={() => {
                    setConfirmaStergere(false);
                    setMotivStergere('');
                  }}
                >
                  Renunță
                </button>
                <button
                  type="button"
                  className="hit-24 chip font-medium text-neg"
                  disabled={motivStergere.trim().length < 3 || sterge.isPending}
                  onClick={() =>
                    sterge.mutate(
                      {
                        data,
                        entryId: plata.id,
                        version: plata.version,
                        motiv: motivStergere.trim(),
                      },
                      { onSuccess: onClose },
                    )
                  }
                >
                  {sterge.isPending ? 'Șterg…' : 'Confirmă ștergerea'}
                </button>
              </div>
            </div>
          )}
        </section>
      ) : null}

      <footer className="mt-auto flex flex-col gap-1 border-t border-hairline pt-2 text-11 text-ink-3">
        <span>
          Scadență:{' '}
          <span className="num">{plata.dueDate ? formatDayLabel(plata.dueDate) : '—'}</span>
        </span>
        <span>
          Stare importată: {etichetaStare(plata.statusImported)}
          {plata.statusOverride ? ` · suprascrisă: ${etichetaStare(plata.statusOverride)}` : ''}
        </span>
        <span>
          Total în lei: <Money minor={plata.amountRonMinor} className="text-11" />
        </span>
      </footer>
    </aside>
  );
}
